import React, { useCallback, useContext } from 'react';
import { useCoupon } from 'src/core/state/cart/coupon';
import { Dropdown } from 'src/components/Dropdown/Dropdown';
import { Input } from 'src/components/Forms/Input';
import { Form } from 'src/core/forms';
import { Fieldset } from 'src/components/Forms/Fieldset';
import { Button } from 'src/components/Button/Button';
import { BasketContext } from 'src/components/Basket/BasketContext';
import { Message } from 'src/core/engine/types';

import { Actions } from '../Forms/Actions';
import { Messages } from '../Messages/Messages';

import classes from './BasketActions.scss';

type Props = Record<string, unknown>;

export const CouponContext = React.createContext<{
    pending: boolean;
    messages: Message[];
    applyCoupon(coupon: string);
    removeCoupon();
}>({
    applyCoupon: noop('applyCoupon'),
    pending: false,
    messages: [],
    removeCoupon: noop('removeCoupon'),
});

export const CouponProvider: React.FC<React.PropsWithChildren> = (props) => {
    const { applyCoupon, pending, messages, removeCoupon } = useCoupon();
    return (
        <CouponContext.Provider value={{ applyCoupon, pending, messages, removeCoupon }}>
            {props.children}
        </CouponContext.Provider>
    );
};

export const BasketActions: React.FC<Props> = React.memo(() => {
    const { applyCoupon, pending, messages, removeCoupon } = useContext(CouponContext);
    const { totals } = useContext(BasketContext);
    const current = totals?.discounts || EMPTY_ARR;
    const submit = useCallback(
        (values) => {
            applyCoupon(values.discount);
        },
        [applyCoupon],
    );
    return (
        <div className={classes.basketActions}>
            {messages.length > 0 && (
                <div className={classes.basketActionsMessage}>
                    <Messages messages={messages} />
                </div>
            )}
            <div className={classes.basketActionsPrimary}>
                {current.length === 0 && (
                    <Dropdown buttonText="Add a discount code">
                        <Form onSubmit={submit}>
                            <Fieldset disabled={pending}>
                                <Input label="Discount code" field="discount" id="discount" />
                                <Actions primaryText="Apply" />
                            </Fieldset>
                        </Form>
                    </Dropdown>
                )}
                {current.length > 0 && (
                    <Button onClick={removeCoupon} disabled={pending}>
                        Remove Coupon
                    </Button>
                )}
            </div>
        </div>
    );
});

const EMPTY_ARR = [];

function noop(name: string) {
    return () => {
        // eslint-disable-next-line no-console
        console.log(`${name} is missing - did you include the register function for it?`);
        return undefined;
    };
}
